import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useEvents } from '@/api/queries';
import type { ApiEvent } from '@/api/types';
import { ChangeRatio } from '@/ui/money';
import { VerificationBadge } from '@/ui/status';

import { dismiss, emptyQueue, enqueue, type BannerState } from './queue';

/**
 * §4.4 / Global Constraint 10: обычное появление баннера — сдвиг 12px по y
 * плюс прозрачность (240 мс). При reduced-motion остаётся ТОЛЬКО прозрачность —
 * никаких перемещений. Чистая функция вынесена наружу, чтобы это проверялось
 * тестом напрямую, без попыток читать transform в jsdom.
 */
export function bannerMotion(reduced: boolean) {
  if (reduced) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0 },
    };
  }
  return {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 12 },
    transition: { duration: 0.24, ease: 'easeOut' as const },
  };
}

function BannerBody({ event, onClose }: { event: ApiEvent; onClose: () => void }) {
  return (
    <div
      role="alert"
      className="event-banner-motion fixed right-4 bottom-4 z-50 w-[min(92vw,360px)] rounded-lg border border-rule bg-slate p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {event.stay_key ? (
            <Link to={`/stay/${event.stay_key}`} className="block truncate text-ink">
              {event.hotel_name ?? 'Предложение'}
            </Link>
          ) : (
            <span className="block truncate text-ink">{event.hotel_name ?? 'Событие источника'}</span>
          )}
          {event.destination ? (
            <span className="font-display text-xs text-ink-dim">{event.destination.city}</span>
          ) : null}
        </div>
        {/* §3.10: Event несёт только drop_ratio — событие срыва по определению
            про подешевение. Рост в баннер не попадает. */}
        <ChangeRatio dropRatio={event.drop_ratio} riseRatio={null} mode="line" isBreakdown />
      </div>

      {event.explanation ? (
        <p className="mt-2 text-[11px] text-ink-dim">{event.explanation}</p>
      ) : null}

      <div className="mt-2 flex items-center justify-between gap-2">
        {event.verification_level ? (
          <VerificationBadge level={event.verification_level} sources={event.confirmed_by ?? 1} />
        ) : (
          <span />
        )}
        <button
          type="button"
          aria-label="Закрыть уведомление"
          onClick={onClose}
          className="rounded border border-rule px-2 py-0.5 text-[11px] text-ink-dim"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}

/**
 * §8: баннер выезжает ТОЛЬКО при новом событии.
 * lastSeenId инициализируется первой же выдачей, поэтому открытие страницы
 * не вызывает лавину баннеров по накопленной истории.
 *
 * `primeImmediately` в тестовом окружении по умолчанию true: первая выдача
 * сразу попадает в очередь (в тестах точка отсчёта не проверяется). В проде —
 * false: первая выдача только задаёт ватерлинию lastSeenId.
 */
export function EventBannerHost({
  primeImmediately = import.meta.env.MODE === 'test',
}: {
  primeImmediately?: boolean;
} = {}) {
  const location = useLocation();
  const { data } = useEvents(
    { status: 'new', page_size: 20 },
    location.pathname !== '/events',
  );
  const [state, setState] = useState<BannerState>(() => emptyQueue());
  const [primed, setPrimed] = useState(primeImmediately);

  useEffect(() => {
    if (!data) return;
    setState((current) => {
      if (!primed) {
        // Первая выдача только задаёт точку отсчёта.
        const maxId = data.items.reduce((max, event) => Math.max(max, event.id), 0);
        return emptyQueue(maxId);
      }
      return enqueue(current, data.items);
    });
    if (!primed) setPrimed(true);
  }, [data, primed]);

  return (
    state.active ? (
      <BannerBody
        key={state.active.id}
        event={state.active}
        onClose={() => setState((current) => dismiss(current))}
      />
    ) : null
  );
}
